import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import './VideoInsights.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type RangeKey = '7d' | '30d' | '90d' | 'lifetime';
type BannerTone = 'warn' | 'danger' | 'info' | 'neutral';

interface AnalyticsApiEnvelope<T> {
  success: boolean;
  data: T;
  error?: string;
}

interface LivePostPerformance {
  views: number;
  likes: number;
  comments: number;
  shares: number;
  engagement: number;
  engagementRate: number;
}

interface LivePost {
  id: string;
  publishRequestId: string;
  linkedAccountId: string | null;
  status: string;
  projectName: string | null;
  videoId: string;
  title: string | null;
  caption: string | null;
  platform: string;
  platformPostId: string | null;
  accountName: string;
  publishedAt: string | null;
  createdAt: string | null;
  postUrl: string | null;
  hasInsights: boolean;
  insightsFetchedAt: string | null;
  performance: LivePostPerformance;
}

interface LivePostsResponse {
  posts: LivePost[];
}

interface DashboardVideoPerformance {
  views: number;
  likes: number;
  comments: number;
  shares: number;
  engagement: number;
  engagementRate: number;
  watchMinutes: number;
  avgViewDurationSeconds: number;
  subscribersGained: number;
}

interface DashboardVideoRow {
  id: string;
  platform: string;
  title: string;
  projectName: string | null;
  accountName: string | null;
  platformPostId: string | null;
  postUrl: string | null;
  fetchedAt: string | null;
  performance: DashboardVideoPerformance;
}

interface DashboardVideoList {
  rows: DashboardVideoRow[];
}

interface RetentionPoint {
  videoId: string;
  retentionAvgWatchRatio: number;
  retentionAvgRelativePerformance: number;
  retention30sRatio: number;
  retentionMidpointRatio: number;
  retentionCompletionRatio: number;
}

interface TrafficPoint {
  videoId: string;
  browseViews: number;
  suggestedViews: number;
  searchViews: number;
  browseShare: number;
  suggestedShare: number;
  searchShare: number;
}

interface CountryRow {
  countryCode: string;
  countryName: string;
  views: number;
  engagementRate: number;
}

interface CountryInsights {
  rows: CountryRow[];
}

interface LocationState {
  post?: LivePost;
}

interface BannerDiagnosis {
  tone: BannerTone;
  icon: string;
  title: string;
  description: string;
  tag: string;
}

function formatPlatform(platform: string): string {
  if (platform === 'youtube') return 'YouTube';
  if (platform === 'twitter') return 'X';
  if (platform === 'tiktok') return 'TikTok';
  if (platform === 'facebook') return 'Facebook';
  if (platform === 'linkedin') return 'LinkedIn';
  if (platform === 'instagram') return 'Instagram';
  if (!platform) return 'Unknown';
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

function formatCompact(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function toFiniteNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function toPercent(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (value <= 1) return Math.max(0, Math.min(100, value * 100));
  return Math.max(0, Math.min(100, value));
}

function toPercentLabel(value: number): string {
  return `${value.toFixed(1)}%`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function formatRangeLabel(range: RangeKey): string {
  if (range === '7d') return 'Last 7 Days';
  if (range === '30d') return 'Last 30 Days';
  if (range === '90d') return 'Last 90 Days';
  return 'Lifetime';
}

function formatDuration(seconds: number): string {
  if (seconds <= 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function resolveShare(rawShare: number, views: number, totalViews: number): number {
  const raw = toFiniteNumber(rawShare);
  if (raw > 0) {
    return raw <= 1 ? raw * 100 : raw;
  }
  if (totalViews <= 0 || views <= 0) return 0;
  return (views / totalViews) * 100;
}

function buildDiagnosis(params: {
  views: number;
  engagementRate: number;
  retentionScore: number;
  hookScore: number;
}): BannerDiagnosis {
  const { views, engagementRate, retentionScore, hookScore } = params;

  if (views >= 10000 && hookScore < 35) {
    return {
      tone: 'danger',
      icon: '✕',
      title: 'Misleading packaging or weak hook',
      description: 'Reach is strong, but early retention is low. Tighten the opening and align the first 10 seconds with the title promise.',
      tag: 'Hook problem',
    };
  }

  if (views < 2500 && retentionScore >= 48 && engagementRate >= 3.5) {
    return {
      tone: 'info',
      icon: '↑',
      title: 'Good content, low distribution',
      description: 'Watch behavior is healthy but reach is limited. Improve title/keyword discoverability and repost at stronger audience windows.',
      tag: 'Reach gap',
    };
  }

  if (engagementRate < 2.5) {
    return {
      tone: 'warn',
      icon: '⚠',
      title: 'Weak engagement response',
      description: 'Viewers are watching but interacting less. Add stronger prompts, clearer payoff, and explicit CTA moments.',
      tag: 'Engagement leak',
    };
  }

  return {
    tone: 'info',
    icon: '✓',
    title: 'Healthy video trajectory',
    description: 'Reach and audience response are balanced. Keep this structure and iterate thumbnail/title variants for more upside.',
    tag: 'Performing well',
  };
}

async function fetchEnvelope<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    signal,
  });

  const payload = (await response.json().catch(() => ({
    success: false,
    error: 'Failed to parse server response',
  }))) as Partial<AnalyticsApiEnvelope<T>>;

  if (!response.ok || payload.success === false || payload.data === undefined) {
    throw new Error(payload.error || `Failed request: ${response.status}`);
  }

  return payload.data as T;
}

async function fetchLivePosts(signal?: AbortSignal): Promise<LivePost[]> {
  const response = await fetch(`${API_BASE}/api/posts/live?status=all&limit=500`, {
    credentials: 'include',
    signal,
  });

  const payload = (await response.json().catch(() => ({
    error: 'Failed to parse live posts response',
  }))) as Partial<LivePostsResponse> & { error?: string };

  if (!response.ok) {
    throw new Error(payload.error || `Failed request: ${response.status}`);
  }

  return Array.isArray(payload.posts) ? payload.posts : [];
}

const VideoInsights: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { postId } = useParams<{ postId: string }>();

  const fromAnalyticsInsights = location.pathname.startsWith('/analytics/insights');

  const livePostsRoute = fromAnalyticsInsights
    ? '/analytics/insights'
    : '/posts/live';

  const backButtonLabel = fromAnalyticsInsights
    ? 'Back to Insights'
    : 'Back to Live Posts';

  const state = (location.state || {}) as LocationState;
  const [range, setRange] = useState<RangeKey>('30d');
  const [post, setPost] = useState<LivePost | null>(state.post && state.post.id === postId ? state.post : null);
  const [videoRow, setVideoRow] = useState<DashboardVideoRow | null>(null);
  const [retentionPoint, setRetentionPoint] = useState<RetentionPoint | null>(null);
  const [trafficPoint, setTrafficPoint] = useState<TrafficPoint | null>(null);
  const [countryRows, setCountryRows] = useState<CountryRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPageData = useCallback(async (signal?: AbortSignal) => {
    if (!postId) {
      setError('Missing post identifier.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const posts = await fetchLivePosts(signal);
      const matchedPost = posts.find((item) => item.id === postId) || state.post || null;

      if (!matchedPost) {
        throw new Error('Unable to locate this post in your Live list.');
      }

      setPost(matchedPost);

      const scopedVideoId = matchedPost.platformPostId || matchedPost.videoId;
      const params = new URLSearchParams();
      params.set('rangeKey', range);
      params.set('videoId', scopedVideoId);

      const videoParams = new URLSearchParams(params);
      videoParams.set('limit', '1');
      videoParams.set('offset', '0');

      const countryParams = new URLSearchParams(params);
      countryParams.set('limit', '6');

      const [videosData, retentionData, trafficData, countryData] = await Promise.all([
        fetchEnvelope<DashboardVideoList>(`${API_BASE}/api/analytics/dashboard/videos?${videoParams.toString()}`, signal),
        fetchEnvelope<RetentionPoint[]>(`${API_BASE}/api/analytics/dashboard/retention?${params.toString()}`, signal),
        fetchEnvelope<TrafficPoint[]>(`${API_BASE}/api/analytics/dashboard/traffic-sources?${params.toString()}`, signal),
        fetchEnvelope<CountryInsights>(`${API_BASE}/api/analytics/dashboard/countries?${countryParams.toString()}`, signal),
      ]);

      const matchedVideo = videosData.rows.find((row) => (row.platformPostId || '') === scopedVideoId)
        || videosData.rows[0]
        || null;

      const matchedRetention = retentionData.find((item) => item.videoId === scopedVideoId)
        || retentionData[0]
        || null;

      const matchedTraffic = trafficData.find((item) => item.videoId === scopedVideoId)
        || trafficData[0]
        || null;

      setVideoRow(matchedVideo);
      setRetentionPoint(matchedRetention);
      setTrafficPoint(matchedTraffic);
      setCountryRows(Array.isArray(countryData.rows) ? countryData.rows : []);
    } catch (err: unknown) {
      if ((err as { name?: string }).name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load video insights');
      setVideoRow(null);
      setRetentionPoint(null);
      setTrafficPoint(null);
      setCountryRows([]);
    } finally {
      setLoading(false);
    }
  }, [postId, range, state.post]);

  useEffect(() => {
    const controller = new AbortController();
    void fetchPageData(controller.signal);
    return () => controller.abort();
  }, [fetchPageData]);

  const performance = useMemo(() => {
    const likes = toFiniteNumber(videoRow?.performance.likes ?? post?.performance.likes);
    const comments = toFiniteNumber(videoRow?.performance.comments ?? post?.performance.comments);
    const shares = toFiniteNumber(videoRow?.performance.shares ?? post?.performance.shares);
    const views = toFiniteNumber(videoRow?.performance.views ?? post?.performance.views);
    const engagement = toFiniteNumber(videoRow?.performance.engagement ?? post?.performance.engagement ?? likes + comments + shares);

    return {
      views,
      likes,
      comments,
      shares,
      engagement,
      engagementRate: toFiniteNumber(videoRow?.performance.engagementRate ?? post?.performance.engagementRate),
      watchMinutes: toFiniteNumber(videoRow?.performance.watchMinutes),
      avgViewDurationSeconds: toFiniteNumber(videoRow?.performance.avgViewDurationSeconds),
      subscribersGained: toFiniteNumber(videoRow?.performance.subscribersGained),
    };
  }, [post, videoRow]);

  const retention = useMemo(() => {
    return {
      watchRatio: toPercent(toFiniteNumber(retentionPoint?.retentionAvgWatchRatio)),
      relativePerformance: toPercent(toFiniteNumber(retentionPoint?.retentionAvgRelativePerformance)),
      at30s: toPercent(toFiniteNumber(retentionPoint?.retention30sRatio)),
      midpoint: toPercent(toFiniteNumber(retentionPoint?.retentionMidpointRatio)),
      completion: toPercent(toFiniteNumber(retentionPoint?.retentionCompletionRatio)),
    };
  }, [retentionPoint]);

  const traffic = useMemo(() => {
    const browseViews = toFiniteNumber(trafficPoint?.browseViews);
    const suggestedViews = toFiniteNumber(trafficPoint?.suggestedViews);
    const searchViews = toFiniteNumber(trafficPoint?.searchViews);
    const knownViews = browseViews + suggestedViews + searchViews;
    const fallbackOtherViews = Math.max(0, performance.views - knownViews);
    const totalViews = knownViews + fallbackOtherViews;

    const browseShare = resolveShare(toFiniteNumber(trafficPoint?.browseShare), browseViews, totalViews);
    const suggestedShare = resolveShare(toFiniteNumber(trafficPoint?.suggestedShare), suggestedViews, totalViews);
    const searchShare = resolveShare(toFiniteNumber(trafficPoint?.searchShare), searchViews, totalViews);

    return {
      browseViews,
      suggestedViews,
      searchViews,
      otherViews: fallbackOtherViews,
      totalViews,
      browseShare,
      suggestedShare,
      searchShare,
      otherShare: totalViews > 0 ? (fallbackOtherViews / totalViews) * 100 : 0,
    };
  }, [performance.views, trafficPoint]);

  const health = useMemo(() => {
    const thumbnail = clamp(Math.round(performance.engagementRate * 11), 8, 100);
    const hook = clamp(Math.round(retention.at30s * 0.6 + retention.midpoint * 0.4), 0, 100);
    const retentionScore = clamp(Math.round((retention.watchRatio + retention.midpoint + retention.completion) / 3), 0, 100);
    const reach = performance.views > 0
      ? clamp(Math.round((Math.log10(performance.views + 1) / 5) * 100), 0, 100)
      : 0;

    const total = Math.round(thumbnail * 0.25 + hook * 0.25 + retentionScore * 0.3 + reach * 0.2);

    return {
      total,
      thumbnail,
      hook,
      retention: retentionScore,
      reach,
    };
  }, [performance.engagementRate, performance.views, retention.at30s, retention.completion, retention.midpoint, retention.watchRatio]);

  const diagnosis = useMemo(() => {
    return buildDiagnosis({
      views: performance.views,
      engagementRate: performance.engagementRate,
      retentionScore: health.retention,
      hookScore: health.hook,
    });
  }, [health.hook, health.retention, performance.engagementRate, performance.views]);

  const retentionSeries = useMemo(() => {
    const points = [
      100,
      clamp(Math.max(retention.at30s, retention.watchRatio), 0, 100),
      retention.at30s,
      retention.midpoint,
      clamp((retention.midpoint + retention.completion) / 2, 0, 100),
      retention.completion,
    ];
    return points.map((value) => Number(value.toFixed(1)));
  }, [retention.at30s, retention.completion, retention.midpoint, retention.watchRatio]);

  const retentionPolyline = useMemo(() => {
    if (retentionSeries.length === 0) return '';

    return retentionSeries
      .map((value, index) => {
        const x = (index / Math.max(retentionSeries.length - 1, 1)) * 100;
        const y = 100 - value;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(' ');
  }, [retentionSeries]);

  const topCountryRows = useMemo(() => {
    return countryRows.slice(0, 5);
  }, [countryRows]);

  const title = videoRow?.title || post?.title || 'Untitled video';
  const accountName = videoRow?.accountName || post?.accountName || 'Unknown account';
  const platform = videoRow?.platform || post?.platform || '';
  const projectName = post?.projectName || videoRow?.projectName || 'No Project';
  const postUrl = videoRow?.postUrl || post?.postUrl || null;
  const insightsFetchedAt = post?.insightsFetchedAt || videoRow?.fetchedAt || null;

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content video-insights-main">
        <Header />

        <section className="video-insights-head">
          <div className="video-insights-head-left">
            <button className="video-insights-back-btn" onClick={() => navigate(livePostsRoute)} type="button">
              {backButtonLabel}
            </button>
            <h1>Video Insights</h1>
            <p>{title}</p>
            <div className="video-insights-meta">
              <span>{formatPlatform(platform)}</span>
              <span>{accountName}</span>
              <span>{projectName}</span>
              {insightsFetchedAt ? <span>Synced {new Date(insightsFetchedAt).toLocaleString()}</span> : null}
            </div>
          </div>

          <div className="video-insights-controls">
            <label>
              <span>Range</span>
              <select value={range} onChange={(event) => setRange(event.target.value as RangeKey)}>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="lifetime">Lifetime</option>
              </select>
            </label>
            {postUrl ? (
              <a className="video-insights-open-link" href={postUrl} target="_blank" rel="noreferrer">
                Open on Platform
              </a>
            ) : null}
          </div>
        </section>

        {error ? <div className="video-insights-alert">{error}</div> : null}

        {loading ? (
          <div className="video-insights-loading">Loading video insights...</div>
        ) : (
          <>
            <section className={`video-insights-banner video-insights-banner--${diagnosis.tone}`}>
              <div className="video-insights-banner-icon">{diagnosis.icon}</div>
              <div className="video-insights-banner-body">
                <h2>{diagnosis.title}</h2>
                <p>{diagnosis.description}</p>
              </div>
              <span className="video-insights-banner-tag">{diagnosis.tag}</span>
            </section>

            <section className="video-insights-health">
              <div className="video-insights-section-label">Content Health Score</div>
              <div className="video-insights-health-card">
                <div className="video-insights-health-total">
                  <strong>{health.total}</strong>
                  <span>out of 100</span>
                </div>

                <div className="video-insights-health-breakdown">
                  <div className="video-insights-health-row">
                    <span>Thumbnail</span>
                    <div className="video-insights-health-track"><div style={{ width: `${health.thumbnail}%` }} /></div>
                    <em>{health.thumbnail}</em>
                  </div>
                  <div className="video-insights-health-row">
                    <span>Hook</span>
                    <div className="video-insights-health-track"><div style={{ width: `${health.hook}%` }} /></div>
                    <em>{health.hook}</em>
                  </div>
                  <div className="video-insights-health-row">
                    <span>Retention</span>
                    <div className="video-insights-health-track"><div style={{ width: `${health.retention}%` }} /></div>
                    <em>{health.retention}</em>
                  </div>
                  <div className="video-insights-health-row">
                    <span>Reach</span>
                    <div className="video-insights-health-track"><div style={{ width: `${health.reach}%` }} /></div>
                    <em>{health.reach}</em>
                  </div>
                </div>
              </div>
            </section>

            <section className="video-insights-section">
              <div className="video-insights-section-label">Core Metrics</div>
              <div className="video-insights-metric-grid">
                <article className="video-insights-metric-card">
                  <div className="video-insights-metric-label">Views</div>
                  <div className="video-insights-metric-value">{formatCompact(performance.views)}</div>
                  <div className="video-insights-metric-sub">{formatRangeLabel(range)}</div>
                  <div className="video-insights-metric-bar"><div style={{ width: `${clamp((Math.log10(performance.views + 1) / 5) * 100, 0, 100)}%` }} /></div>
                </article>
                <article className="video-insights-metric-card">
                  <div className="video-insights-metric-label">Engagement</div>
                  <div className="video-insights-metric-value">{formatCompact(performance.engagement)}</div>
                  <div className="video-insights-metric-sub">{performance.engagementRate.toFixed(2)}% rate</div>
                  <div className="video-insights-metric-bar"><div style={{ width: `${clamp(performance.engagementRate * 10, 0, 100)}%` }} /></div>
                </article>
                <article className="video-insights-metric-card">
                  <div className="video-insights-metric-label">Watch Time</div>
                  <div className="video-insights-metric-value">{formatCompact(performance.watchMinutes)}m</div>
                  <div className="video-insights-metric-sub">Total minutes watched</div>
                  <div className="video-insights-metric-bar"><div style={{ width: `${clamp((performance.watchMinutes / 1500) * 100, 0, 100)}%` }} /></div>
                </article>
                <article className="video-insights-metric-card">
                  <div className="video-insights-metric-label">Avg View Duration</div>
                  <div className="video-insights-metric-value">{formatDuration(performance.avgViewDurationSeconds)}</div>
                  <div className="video-insights-metric-sub">Per play average</div>
                  <div className="video-insights-metric-bar"><div style={{ width: `${clamp((performance.avgViewDurationSeconds / 120) * 100, 0, 100)}%` }} /></div>
                </article>
                <article className="video-insights-metric-card">
                  <div className="video-insights-metric-label">Completion</div>
                  <div className="video-insights-metric-value">{toPercentLabel(retention.completion)}</div>
                  <div className="video-insights-metric-sub">Estimated completion ratio</div>
                  <div className="video-insights-metric-bar"><div style={{ width: `${retention.completion}%` }} /></div>
                </article>
              </div>
            </section>

            <section className="video-insights-grid">
              <article className="video-insights-panel">
                <div className="video-insights-panel-head">
                  <h3>Audience Retention Curve</h3>
                  <span>Drop-off trend through the video</span>
                </div>

                <div className="video-insights-retention-chart">
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-label="Retention curve">
                    <polyline points={retentionPolyline} className="video-insights-retention-line" />
                  </svg>
                </div>

                <div className="video-insights-retention-stats">
                  <div><span>30s</span><strong>{toPercentLabel(retention.at30s)}</strong></div>
                  <div><span>Midpoint</span><strong>{toPercentLabel(retention.midpoint)}</strong></div>
                  <div><span>Completion</span><strong>{toPercentLabel(retention.completion)}</strong></div>
                  <div><span>Avg Watch</span><strong>{toPercentLabel(retention.watchRatio)}</strong></div>
                  <div><span>Relative</span><strong>{toPercentLabel(retention.relativePerformance)}</strong></div>
                </div>
              </article>

              <article className="video-insights-panel">
                <div className="video-insights-panel-head">
                  <h3>Traffic Sources</h3>
                  <span>Where views came from</span>
                </div>

                <div className="video-insights-traffic-list">
                  <div className="video-insights-traffic-row">
                    <div className="video-insights-traffic-label"><strong>Browse</strong><span>{formatNumber(traffic.browseViews)} views</span></div>
                    <div className="video-insights-traffic-track"><div style={{ width: `${clamp(traffic.browseShare, 0, 100)}%` }} /></div>
                    <em>{toPercentLabel(clamp(traffic.browseShare, 0, 100))}</em>
                  </div>
                  <div className="video-insights-traffic-row">
                    <div className="video-insights-traffic-label"><strong>Suggested</strong><span>{formatNumber(traffic.suggestedViews)} views</span></div>
                    <div className="video-insights-traffic-track"><div style={{ width: `${clamp(traffic.suggestedShare, 0, 100)}%` }} /></div>
                    <em>{toPercentLabel(clamp(traffic.suggestedShare, 0, 100))}</em>
                  </div>
                  <div className="video-insights-traffic-row">
                    <div className="video-insights-traffic-label"><strong>Search</strong><span>{formatNumber(traffic.searchViews)} views</span></div>
                    <div className="video-insights-traffic-track"><div style={{ width: `${clamp(traffic.searchShare, 0, 100)}%` }} /></div>
                    <em>{toPercentLabel(clamp(traffic.searchShare, 0, 100))}</em>
                  </div>
                  <div className="video-insights-traffic-row">
                    <div className="video-insights-traffic-label"><strong>Other</strong><span>{formatNumber(traffic.otherViews)} views</span></div>
                    <div className="video-insights-traffic-track"><div style={{ width: `${clamp(traffic.otherShare, 0, 100)}%` }} /></div>
                    <em>{toPercentLabel(clamp(traffic.otherShare, 0, 100))}</em>
                  </div>
                </div>
              </article>
            </section>

            <section className="video-insights-grid">
              <article className="video-insights-panel">
                <div className="video-insights-panel-head">
                  <h3>Engagement</h3>
                  <span>Post-level actions and rate</span>
                </div>

                <div className="video-insights-engagement-grid">
                  <div><span>Likes</span><strong>{formatCompact(performance.likes)}</strong></div>
                  <div><span>Comments</span><strong>{formatCompact(performance.comments)}</strong></div>
                  <div><span>Shares</span><strong>{formatCompact(performance.shares)}</strong></div>
                  <div><span>Engagement Rate</span><strong>{performance.engagementRate.toFixed(2)}%</strong></div>
                  <div><span>Subs Gained</span><strong>{formatCompact(performance.subscribersGained)}</strong></div>
                  <div><span>Publish ID</span><strong>{post?.publishRequestId || 'n/a'}</strong></div>
                </div>
              </article>

              <article className="video-insights-panel">
                <div className="video-insights-panel-head">
                  <h3>Views by Country</h3>
                  <span>Top geographies in selected range</span>
                </div>

                {topCountryRows.length === 0 ? (
                  <div className="video-insights-empty-country">No country breakdown available for this video and range.</div>
                ) : (
                  <div className="video-insights-country-list">
                    {topCountryRows.map((country) => (
                      <div key={country.countryCode} className="video-insights-country-row">
                        <div>
                          <strong>{country.countryName}</strong>
                          <span>{country.countryCode}</span>
                        </div>
                        <div>
                          <strong>{formatCompact(country.views)} views</strong>
                          <span>{country.engagementRate.toFixed(2)}% ER</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default VideoInsights;
